import { ColumnSort, getToggledSortDirection } from "JS/Types";
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { styled } from "@mui/material/styles";
import { InternalStandardProps } from "@mui/material";

const IconWrapper = styled("span")(({ theme }) => ({
    margin: theme.spacing(0, 1),
}));

export function sortableHeader<T>(Component: React.ComponentType<T> | string) {
    type SortDirection = "asc" | "desc";
    interface Props extends InternalStandardProps<{}> {
        sort: ColumnSort;
        upDirection: SortDirection;
        downDirection: SortDirection;
        onSortChanged: (sort: ColumnSort, oldOrder: number) => void;
        children: React.ReactNode;
    }

    type HOCProps = OwnProps;
    type OwnProps = Props & Partial<Omit<T, "children">>; //Props that are allowed to passed from Resulting component returned from HOC.

    const StyledComponent = styled(Component as React.ComponentType<any>, {
        shouldForwardProp: (prop) => prop !== "sorted",
    })<{ sorted?: boolean }>(({ theme, sorted }) => ({
        color: sorted ? theme.palette.grey[600] : theme.palette.grey[300],
        display: "flex",
        cursor: "pointer",
        fontSize: "1rem",
        fontWeight: 500,

        "&:hover": {
            color: theme.palette.grey[600],
        },
    })) as React.ComponentType<any>;

    function SortableHeader(props: HOCProps) {
        const { sort, onSortChanged = () => {}, ...rest } = props;
        const {
            children,
            className,
            upDirection,
            downDirection,
            ...cellProps
        } = rest;
        const tsBypass: T = cellProps as any;

        const onSortChange = (e) => {
            onSortChanged(
                {
                    ...sort,
                    dir: getToggledSortDirection(sort.col, sort.dir),
                },
                sort.position,
            );
        };

        return (
            <StyledComponent
                {...tsBypass}
                onClick={onSortChange}
                className={className}
                sorted={!!sort.dir}
            >
                {children}{" "}
                <IconWrapper>
                    {sort.dir === downDirection && (
                        <FontAwesomeIcon icon={["fas", "arrow-down"]} />
                    )}
                    {sort.dir === upDirection && (
                        <FontAwesomeIcon icon={["fas", "arrow-up"]} />
                    )}
                </IconWrapper>
            </StyledComponent>
        );
    }

    return SortableHeader;
}

export default sortableHeader;
